import { createContext, type Dispatch, type SetStateAction, useLayoutEffect } from "react";

import type { HaNMapLayer } from "@lib/logic";
import { useRequiredContext } from "@lib/utils";

import type { HaNTabMapLayer } from "../../map/HaNMapFrame";
import { HaNContext } from "../HaNContext";

type HaNMapStageContextValue = {
  floorId: string | null;
  setTabLayer: Dispatch<SetStateAction<HaNTabMapLayer | null>>;
};

// The floor to draw a map of (the selected floor, or `null` when it has no map), and where tabs hand in their layer
export const HaNMapStageContext = createContext<HaNMapStageContextValue | null>(null);

/** Shows a tab's layer on the stage's map. Switching tabs swaps the layer, so the map itself is kept. */
export const useHaNMapLayer = (layer: HaNMapLayer) => {
  const { tab } = useRequiredContext(HaNContext);
  const { setTabLayer } = useRequiredContext(HaNMapStageContext);

  // A layout effect, so the map has the new layer before the browser paints
  useLayoutEffect(
    function showLayerOnMap() {
      const tabLayer = { tab, layer };

      setTabLayer(tabLayer);

      return () => {
        setTabLayer(current => (current === tabLayer ? null : current));
      };
    },
    [layer, setTabLayer, tab],
  );
};
